import { KeyboardEventHandler, useCallback, useState } from 'react';

const focusableNodeTypes = ['input', 'select', 'textarea', 'button', 'a'];

const isFocusable = (node: Element) => {
  if (node.getAttribute('tab-index') === '-1') {
    return false;
  }

  if (node.hasAttribute('disabled')) {
    return node.getAttribute('disabled') === 'false';
  }

  if (focusableNodeTypes.includes(node.nodeName.toLowerCase())) {
    return true;
  }

  return false;
};

const deepFindNode = (
  nodes: Element[],
  findFn: (node: Element) => boolean,
): boolean => {
  return nodes.some((node) => {
    if (findFn(node)) {
      return true;
    }

    if (node.children.length === 0) {
      return false;
    }
    return deepFindNode(Array.from(node.children), findFn);
  });
};

/**
 * Supports interactive dropdown menu elements by keeping the menu open when we
 * have selected items, and we're tabbing into the footer or group filter.
 */
export default ({ id }: { id: string }) => {
  const [keepMenuOpenOnBlur, setKeepMenuOpenOnBlur] = useState(false);

  const onKeyDown: KeyboardEventHandler<HTMLElement> = useCallback(
    (e) => {
      if (e.key === 'Tab') {
        const ids = [`${id}-dropdown-footer`, `${id}-group-filter`];

        const hasFocusableChild = ids.some((id) => {
          const el = document.getElementById(id);
          const childrenArray = el?.children ? Array.from(el.children) : [];
          return deepFindNode(childrenArray, isFocusable);
        });

        setKeepMenuOpenOnBlur(hasFocusableChild);
      }
    },
    [id],
  );

  const getShouldKeepMenuOpenOnBlur = useCallback(() => {
    const toReturn = keepMenuOpenOnBlur;

    setKeepMenuOpenOnBlur(false);

    return toReturn;
  }, [keepMenuOpenOnBlur]);

  return { onKeyDown, getShouldKeepMenuOpenOnBlur };
};
